const BUILDER_SELECTOR = "#MainContent[data-template^='product'] .smart-personalization[data-visual-builder='true']";

export function manageModalFocus(modal) {
  const doc = modal.ownerDocument;
  const trigger = doc.activeElement;
  const changed = [];
  let branch = modal;
  while (branch.parentElement) {
    for (const sibling of branch.parentElement.children) {
      if (sibling !== branch && !sibling.inert) {
        changed.push(sibling);
        sibling.inert = true;
      }
    }
    branch = branch.parentElement;
  }
  const controls = () => Array.from(modal.querySelectorAll('button:not([disabled]), input:not([disabled]), select:not([disabled]), a[href], [tabindex="0"]'))
    .filter(el => el.getClientRects().length);
  const keydown = event => {
    if (event.key !== 'Tab') return;
    const items = controls();
    const first = items[0], last = items[items.length - 1];
    if (!first) { event.preventDefault(); return; }
    if (event.shiftKey && (doc.activeElement === first || !modal.contains(doc.activeElement))) {
      event.preventDefault(); last.focus();
    } else if (!event.shiftKey && (doc.activeElement === last || !modal.contains(doc.activeElement))) {
      event.preventDefault(); first.focus();
    }
  };
  modal.addEventListener('keydown', keydown);
  controls()[0]?.focus();
  return () => {
    modal.removeEventListener('keydown', keydown);
    changed.forEach(el => { el.inert = false; });
    if (trigger?.isConnected) trigger.focus();
  };
}

export function setupPickerFocus(root) {
  const modal = root.querySelector('#builderPickerModal');
  if (!modal || modal.dataset.focusManaged) return;
  modal.dataset.focusManaged = 'true';
  let release;
  const sync = () => {
    const open = !modal.hidden && modal.classList.contains('is-open');
    if (open && !release) release = manageModalFocus(modal);
    if (!open && release) { release(); release = null; }
  };
  new MutationObserver(sync).observe(modal, {attributes: true, attributeFilter: ['hidden', 'class']});
  sync();
}

export async function refreshMarketCatalog(root) {
  const data = root.closest('product-form-component')?.querySelector('#bm-variant-json') || root.querySelector('#bm-variant-json');
  if (!data || !location.pathname.includes('/products/')) return;
  try {
    const response = await fetch(location.pathname + '.js', {cache: 'no-store'});
    if (!response.ok) return;
    const product = await response.json();
    if (!Array.isArray(product.variants) || !product.variants.length) return;
    const previous = JSON.parse(data.textContent);
    if (!previous.some(v => v.id === product.variants[0].id)) return;
    data.textContent = JSON.stringify(product.variants);
    const find = (figures, addons) => product.variants.find(v => v.option1 === String(figures) && v.option2 === String(addons) && (v.option3 || 'No') === 'No');
    const graduation = product.variants.every(v => !v.option2) && product.handle === 'graduation-minifigure-gift';
    const base = graduation ? product.variants.find(v => v.option1 === '0') : find(1, 0);
    const extra = graduation ? null : find(2, 0);
    const addon = graduation ? product.variants.find(v => v.option1 === '1') : find(1, 1);
    const money = cents => new Intl.NumberFormat(undefined, {style:'currency', currency:window.Shopify?.currency?.active || 'USD'}).format(cents / 100);
    const figures = root.querySelector('#figureBlocks');
    if (base && addon) {
      const label = '+' + money(addon.price - base.price);
      if (figures) figures.dataset.addonPriceLabel = label;
      root.querySelectorAll('.bm-pet-toggle .bm-price-label, .bm-addon-toggle .bm-price-label').forEach(el => {el.textContent = '(' + label + ')';});
    }
    if (base && extra) {
      const label = '+' + money(extra.price - base.price);
      if (figures) figures.dataset.figurePriceLabel = label;
      root.querySelectorAll('#addFigureBtn .bm-price-label, .add-figure-btn .bm-price-label').forEach(el => {el.textContent = '(' + label + ')';});
    }
  } catch (_) { /* Keep the server-rendered catalogue on a network failure. */ }
}

export function getVariantFigureLimit(variants, fallback = 6) {
  const counts = Array.isArray(variants)
    ? variants
        .map((variant) => Number.parseInt(String(variant?.option1 ?? '').trim(), 10))
        .filter((count) => Number.isFinite(count) && count > 0)
    : [];

  return counts.length ? Math.max(...counts) : fallback;
}

export function getFigureCountLabel(count) {
  const normalizedCount = Math.max(0, Number.parseInt(String(count || 0), 10) || 0);
  return `${normalizedCount} ${normalizedCount === 1 ? 'figure' : 'figures'}`;
}

export function getFigureLimitMessage(limit) {
  const normalizedLimit = Math.max(1, Number.parseInt(String(limit || 1), 10) || 1);
  return `Need more than ${normalizedLimit} figures? Contact us for a custom order.`;
}

export function normalizeProductHeadingHierarchy(scope) {
  const main = scope?.matches?.("#MainContent[data-template^='product']")
    ? scope
    : scope?.querySelector?.("#MainContent[data-template^='product']") ||
      scope?.closest?.("#MainContent[data-template^='product']");
  const headings = main?.querySelectorAll?.('.accordion-content > h4') || [];
  let normalized = 0;

  headings.forEach((heading) => {
    const documentRef = heading.ownerDocument || globalThis.document;
    const replacement = documentRef?.createElement('h3');
    if (!replacement) return;

    Array.from(heading.attributes || []).forEach(({ name, value }) => {
      replacement.setAttribute(name, value);
    });
    while (heading.firstChild) replacement.append(heading.firstChild);
    heading.replaceWith(replacement);
    normalized += 1;
  });

  return normalized;
}

export function applyAiStockCopy(root) {
  const panels = root?.querySelectorAll?.('.figure-ai-match') || [];
  panels.forEach((panel) => {
    const title = panel.querySelector?.('.fig-ai-title');
    const titleCopy = 'Or let AI suggest pieces from our current stock';
    if (title && title.textContent !== titleCopy) title.textContent = titleCopy;

    let help = panel.querySelector?.('.fig-ai-help');
    if (!help && title && globalThis.document?.createElement) {
      help = globalThis.document.createElement('p');
      help.className = 'fig-ai-help';
      title.insertAdjacentElement('afterend', help);
    }
    const helpCopy = 'Upload a photo to find the closest available hair, face, and outfit matches.';
    if (help && help.textContent !== helpCopy) help.textContent = helpCopy;

    const button = panel.querySelector?.('.fig-ai-upload');
    if (button) {
      const isAnotherPhoto = /another/i.test(String(button.textContent || ''));
      const buttonCopy = isAnotherPhoto ? 'Upload another photo & find matches' : 'Upload photo & find matches';
      if (button.textContent !== buttonCopy) button.textContent = buttonCopy;
    }
  });
  return panels.length;
}

function watchAiStockCopy(root, Observer = globalThis.MutationObserver) {
  if (!root || typeof Observer !== 'function' || root.dataset.tfrAiCopyWatcher === 'true') return null;
  root.dataset.tfrAiCopyWatcher = 'true';
  const observer = new Observer(() => applyAiStockCopy(root));
  observer.observe(root, { childList: true, characterData: true, subtree: true });
  return observer;
}

export function applyPickerSpacing(root) {
  const pickerGrid = root?.querySelector?.('.builder-modal-grid');
  if (!pickerGrid) return;

  if (pickerGrid.style.getPropertyValue('gap') !== '14px' || pickerGrid.style.getPropertyPriority('gap') !== 'important') {
    pickerGrid.style.setProperty('gap', '14px', 'important');
  }
  if (
    pickerGrid.style.getPropertyValue('padding') !== '14px' ||
    pickerGrid.style.getPropertyPriority('padding') !== 'important'
  ) {
    pickerGrid.style.setProperty('padding', '14px', 'important');
  }
  if (
    pickerGrid.style.getPropertyValue('grid-auto-rows') !== 'auto' ||
    pickerGrid.style.getPropertyPriority('grid-auto-rows') !== 'important'
  ) {
    pickerGrid.style.setProperty('grid-auto-rows', 'auto', 'important');
  }

  pickerGrid.querySelectorAll?.('.builder-option-btn').forEach((card) => {
    if (card.style.getPropertyValue('grid-row') !== 'auto' || card.style.getPropertyPriority('grid-row') !== 'important') {
      card.style.setProperty('grid-row', 'auto', 'important');
    }
  });
}

export function watchPickerSpacing(root, Observer = globalThis.MutationObserver) {
  const pickerGrid = root?.querySelector?.('.builder-modal-grid');
  if (!pickerGrid || typeof Observer !== 'function') return null;

  const observer = new Observer(() => applyPickerSpacing(root));
  observer.observe(pickerGrid, {
    attributes: true,
    attributeFilter: ['style'],
    childList: true,
    subtree: true,
  });
  return observer;
}

export function applyEditModeButtonLabels(scope, search = globalThis.location?.search || '') {
  const params = new URLSearchParams(search);
  const hasLineReference = Boolean(
    params.get('line_item_key') || params.get('line_item') || params.get('edit_cart_item')
  );
  if (params.get('bm_edit') !== '1' || !hasLineReference) return 0;

  const buttons = scope?.querySelectorAll?.(
    'product-form-component add-to-cart-component button, product-form-component .add-to-cart-button.p_a_t_c, product-form-component button.p_a_t_c, product-form-component input.p_a_t_c'
  ) || [];

  buttons.forEach((button) => {
    const text = button.querySelector?.('.add-to-cart-text__content');
    if (text && text.textContent !== 'Update design') text.textContent = 'Update design';
    if (typeof globalThis.HTMLInputElement === 'function' && button instanceof globalThis.HTMLInputElement) {
      button.value = 'Update design';
    }
    button.setAttribute?.('aria-label', 'Update design');
  });

  return buttons.length;
}

function watchEditModeButtonLabels(scope, Observer = globalThis.MutationObserver) {
  const search = globalThis.location?.search || '';
  if (!search.includes('bm_edit=1')) return null;

  applyEditModeButtonLabels(scope, search);
  const target = scope?.querySelector?.('product-form-component');
  if (!target || typeof Observer !== 'function' || target.dataset.tfrEditLabelWatcher === 'true') return null;

  target.dataset.tfrEditLabelWatcher = 'true';
  const observer = new Observer(() => applyEditModeButtonLabels(scope, search));
  observer.observe(target, { childList: true, characterData: true, subtree: true });
  return observer;
}

function getStatus(panel) {
  const fields = ['.fig-face', '.fig-hair', '.fig-top', '.fig-bottom']
    .map((selector) => panel.querySelector(selector))
    .filter(Boolean);
  const selected = fields.filter((field) => String(field.value || '').trim()).length;

  if (fields.length && selected === fields.length) return 'Ready';
  if (selected) return `${selected} of ${fields.length} chosen`;
  return 'Start here';
}

function safeIdPart(value) {
  return String(value || 'product').replace(/[^a-zA-Z0-9_-]/g, '-');
}

function setupBuilder(root) {
  if (root.dataset.tfrBuilderEnhanced === 'true') return;

  const figureBlocks = root.querySelector('#figureBlocks');
  if (!figureBlocks) return;

  root.dataset.tfrBuilderEnhanced = 'true';
  root.classList.add('tfr-builder-enhanced');
  setupPickerFocus(root);
  refreshMarketCatalog(root);
  window.addEventListener('pageshow', event => { if (event.persisted) refreshMarketCatalog(root); });
  const price = root.closest('main')?.querySelector('product-price');
  if (price && !price.parentElement.querySelector('[data-builder-jump]')) {
    const jump = document.createElement('a');
    jump.href = '#smartPersonalization';
    jump.className = 'tf-builder-jump';
    jump.dataset.builderJump = '';
    jump.textContent = 'Start customizing ↓';
    jump.addEventListener('click', () => {
      root.setAttribute('tabindex', '-1');
      root.focus({preventScroll: true});
    });
    price.insertAdjacentElement('afterend', jump);
  }
  applyPickerSpacing(root);
  watchPickerSpacing(root);
  applyAiStockCopy(root);
  watchAiStockCopy(root);

  const navigation = document.createElement('section');
  navigation.className = 'tfr-figure-navigation';
  navigation.setAttribute('aria-label', 'Figure editor');

  const header = document.createElement('div');
  header.className = 'tfr-figure-navigation__header';
  header.innerHTML = `
    <div>
      <h2 class="tfr-figure-navigation__heading">Build your figures</h2>
      <p class="tfr-figure-navigation__help">Edit one figure at a time. Your choices stay saved as you switch.</p>
    </div>
    <span class="tfr-figure-navigation__count" aria-live="polite"></span>
  `;

  const tabList = document.createElement('div');
  tabList.className = 'tfr-figure-tabs';
  tabList.setAttribute('role', 'tablist');
  tabList.setAttribute('aria-label', 'Choose a figure to edit');
  navigation.append(header, tabList);
  figureBlocks.before(navigation);

  const sectionId = safeIdPart(root.closest('product-form-component')?.dataset.sectionId);
  const countLabel = navigation.querySelector('.tfr-figure-navigation__count');
  const variantData = document.getElementById('bm-variant-json');
  let variants = [];
  try {
    variants = JSON.parse(variantData?.textContent || '[]');
  } catch (_error) {
    variants = [];
  }
  const figureLimit = getVariantFigureLimit(variants);
  const addFigureButton = root.querySelector('#addFigureBtn');
  const limitNotice = document.createElement('p');
  limitNotice.className = 'tfr-figure-limit-notice';
  limitNotice.setAttribute('aria-live', 'polite');
  limitNotice.hidden = true;
  limitNotice.append(`Need more than ${figureLimit} figures? `);
  const contactLink = document.createElement('a');
  contactLink.href = '/pages/contact';
  contactLink.textContent = 'Contact us';
  limitNotice.append(contactLink, ' for a custom order.');
  addFigureButton?.insertAdjacentElement('afterend', limitNotice);
  let activeFigure = '';

  function panels() {
    return Array.from(figureBlocks.children).filter((element) => element.classList.contains('figure-block'));
  }

  function activate(figure, options = {}) {
    const allPanels = panels();
    const target = allPanels.find((panel) => panel.dataset.figure === String(figure)) || allPanels[0];
    if (!target) return;

    activeFigure = target.dataset.figure;
    allPanels.forEach((panel) => {
      const selected = panel === target;
      panel.hidden = !selected;
      panel.setAttribute('aria-hidden', String(!selected));
    });

    Array.from(tabList.children).forEach((tab) => {
      const selected = tab.dataset.figure === activeFigure;
      tab.setAttribute('aria-selected', String(selected));
      tab.tabIndex = selected ? 0 : -1;
      if (selected && options.focus) tab.focus();
      if (selected && options.scroll) tab.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
    });
  }

  function updateStatuses() {
    const byFigure = new Map(panels().map((panel) => [panel.dataset.figure, panel]));
    Array.from(tabList.children).forEach((tab) => {
      const panel = byFigure.get(tab.dataset.figure);
      const status = tab.querySelector('.tfr-figure-tab__status');
      if (panel && status) status.textContent = getStatus(panel);
    });
  }

  function render(preferredFigure = activeFigure) {
    const allPanels = panels();
    tabList.replaceChildren();

    allPanels.forEach((panel, index) => {
      const figure = String(panel.dataset.figure || index + 1);
      panel.dataset.figure = figure;
      panel.id = `tfr-figure-panel-${sectionId}-${safeIdPart(figure)}`;
      panel.setAttribute('role', 'tabpanel');
      panel.setAttribute('aria-labelledby', `tfr-figure-tab-${sectionId}-${safeIdPart(figure)}`);

      const tab = document.createElement('button');
      tab.type = 'button';
      tab.className = 'tfr-figure-tab';
      tab.id = `tfr-figure-tab-${sectionId}-${safeIdPart(figure)}`;
      tab.dataset.figure = figure;
      tab.setAttribute('role', 'tab');
      tab.setAttribute('aria-controls', panel.id);
      tab.innerHTML = `
        <span class="tfr-figure-tab__number" aria-hidden="true">${index + 1}</span>
        <span class="tfr-figure-tab__label">Figure ${index + 1}</span>
        <span class="tfr-figure-tab__status">${getStatus(panel)}</span>
      `;
      tab.addEventListener('click', () => activate(figure, { scroll: true }));
      tab.addEventListener('keydown', (event) => {
        const tabs = Array.from(tabList.children);
        const current = tabs.indexOf(tab);
        let next = current;
        if (event.key === 'ArrowLeft') next = (current - 1 + tabs.length) % tabs.length;
        if (event.key === 'ArrowRight') next = (current + 1) % tabs.length;
        if (event.key === 'Home') next = 0;
        if (event.key === 'End') next = tabs.length - 1;
        if (next === current || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        activate(tabs[next].dataset.figure, { focus: true, scroll: true });
      });
      tabList.append(tab);
    });

    if (countLabel) countLabel.textContent = getFigureCountLabel(allPanels.length);
    limitNotice.hidden = allPanels.length < figureLimit;
    activate(preferredFigure);
  }

  figureBlocks.addEventListener('input', updateStatuses);
  figureBlocks.addEventListener('change', updateStatuses);
  figureBlocks.addEventListener('click', () => window.setTimeout(updateStatuses, 0));

  const observer = new MutationObserver((records) => {
    let addedFigure = '';
    let changed = false;
    records.forEach((record) => {
      record.addedNodes.forEach((node) => {
        if (node.nodeType === Node.ELEMENT_NODE && node.classList.contains('figure-block')) {
          addedFigure = node.dataset.figure || '';
          changed = true;
        }
      });
      record.removedNodes.forEach((node) => {
        if (node.nodeType === Node.ELEMENT_NODE && node.classList.contains('figure-block')) changed = true;
      });
    });
    if (changed) render(addedFigure || activeFigure);
  });
  observer.observe(figureBlocks, { childList: true });

  render();
}

function initialize(scope) {
  normalizeProductHeadingHierarchy(scope);
  watchEditModeButtonLabels(scope);
  scope.querySelectorAll(BUILDER_SELECTOR).forEach(setupBuilder);
}

if (typeof document !== 'undefined') {
  initialize(document);
  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
}
